// An inline (in-page, not full-route) rendering of a RouteStateKind's copy
// -- the same honest-state vocabulary as RouteRecoveryScreen, for a card or
// strip that sits alongside otherwise-loaded content (e.g. the accounts
// needs-attention strip, or a sync-health pill's expanded detail) rather
// than replacing the whole screen. The copy itself comes from
// ROUTE_STATE_CONTENT in RouteStates.tsx -- one source of truth, so inline
// and full-screen wording can never drift apart.
import { ROUTE_STATE_CONTENT, type RouteStateKind } from '@/app/states/RouteStates';

const INLINE_KINDS = [
  'stale-snapshot',
  'item-needs-login',
  'item-revoked',
  'sync-failed',
  'item-pending-expiration',
  'likely-transfer-review',
  'draft-expired',
  'preview-superseded',
  'no-data-in-range',
  'inbox-zero',
  'awaiting-first-snapshot',
] as const satisfies readonly RouteStateKind[];

export type InlineStateKind = (typeof INLINE_KINDS)[number];

// INLINE_KINDS documents which kinds the plan (§6.2) says are reachable as an
// inline treatment; the copy for any kind still resolves from the one shared
// map, so a caller passing another kind gets correct wording, not a fallback.
function inlineContent(kind: RouteStateKind) {
  return ROUTE_STATE_CONTENT[kind];
}


export function HonestInline({
  kind,
  action,
  className,
}: {
  kind: RouteStateKind;
  action?: { label: string; onAction: () => void };
  className?: string;
}) {
  const content = inlineContent(kind);

  return (
    <div
      role="status"
      className={[
        'rounded-lg border border-border-strong bg-card p-4 text-sm',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="honest-inline"
      data-kind={kind}
    >
      <div className="font-semibold">{content.title}</div>
      <p className="mt-1 text-muted-foreground">{content.body}</p>
      {action && (
        <button
          type="button"
          onClick={action.onAction}
          className="mt-3 rounded-md bg-primary px-3 py-1.5 text-primary-foreground"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}

export default HonestInline;
